import { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/auth/AuthContext";
import { BrandLogo } from "@/components/BrandLogo";
import { Button } from "@/components/ui/button";
import { useQuery } from "@tanstack/react-query";
import { fetchProfile } from "@/api/userAccount";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "@/components/ui/dropdown-menu";
import { LogOut, Sun, Moon, Menu, X, LayoutDashboard } from "lucide-react";
import { cn } from "@/lib/utils";

export function PublicHeader() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(() =>
    typeof document !== "undefined"
      ? document.documentElement.classList.contains("dark")
      : false
  );
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  useEffect(() => {
    document.documentElement.classList.toggle("dark", darkMode);
  }, [darkMode]);

  const profileQuery = useQuery({
    queryKey: ["profile_topbar_public"],
    queryFn: () => fetchProfile(),
    enabled: Boolean(user),
    staleTime: 60 * 1000,
  });

  const profile = profileQuery.data?.profile ?? null;
  const firstName = profile?.firstName?.trim() ?? "";
  const lastName = profile?.lastName?.trim() ?? "";
  const fullName = [firstName, lastName].filter(Boolean).join(" ").trim();
  const emailPrefix = user?.email ? user.email.split("@")[0] : "";
  const displayName = fullName || emailPrefix || "User";
  const avatarUrl = profile?.image ?? null;

  const roleLabel = user?.role
    ? user.role[0].toUpperCase() + user.role.slice(1)
    : "";

  const startHref =
    user?.role === "admin"
      ? "/admin"
      : user?.role === "teacher"
      ? "/teacher"
      : "/dashboard";

  const handleScrollTo = (id: string) => {
    setMobileMenuOpen(false);
    if (window.location.pathname !== "/") {
      navigate(`/${id}`);
      return;
    }
    const element = document.querySelector(id);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="sticky top-0 z-40 w-full bg-background/80 backdrop-blur-md border-b border-border/60">
      <div className="flex items-center justify-between px-6 md:px-8 py-3.5 max-w-7xl mx-auto">
        {/* Left: Logo */}
        <BrandLogo asLinkTo="/" size="md" />

        {/* Center: Navigation Links (Desktop) */}
        <div className="hidden md:flex items-center gap-6 text-sm font-medium text-muted-foreground">
          <button
            onClick={() => {
              if (window.location.pathname !== "/") {
                navigate("/");
              } else {
                window.scrollTo({ top: 0, behavior: "smooth" });
              }
            }}
            className="hover:text-foreground transition-colors cursor-pointer"
          >
            Home
          </button>
          <button
            onClick={() => handleScrollTo("#courses")}
            className="hover:text-foreground transition-colors cursor-pointer"
          >
            Courses
          </button>
          <Link
            to="/mock-tests"
            className="hover:text-foreground transition-colors cursor-pointer"
          >
            Mock Tests
          </Link>

          <button
            onClick={() => handleScrollTo("#contact")}
            className="hover:text-foreground transition-colors cursor-pointer"
          >
            Contact
          </button>
        </div>

        {/* Right: Actions */}
        <div className="flex items-center gap-3">
          {/* Dark Mode Toggle */}
          <button
            onClick={() => setDarkMode(!darkMode)}
            className="w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            aria-label="Toggle dark mode"
          >
            {darkMode ? <Sun size={18} /> : <Moon size={18} />}
          </button>

          {/* Guest CTAs vs Logged In Profile */}
          {!user ? (
            <div className="hidden md:flex items-center gap-3">
              <Link to="/login">
                <Button variant="ghost" size="sm" className="font-medium">
                  Login
                </Button>
              </Link>
              <Link to="/register">
                <Button size="sm" className="font-semibold px-4">
                  Register
                </Button>
              </Link>
            </div>
          ) : (
            <div className="hidden md:flex items-center gap-3">
              <Link to={startHref}>
                <Button variant="secondary" size="sm" className="gap-1.5 font-medium">
                  <LayoutDashboard size={14} />
                  Dashboard
                </Button>
              </Link>

              {/* Profile Dropdown */}
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button className="h-9 px-2 flex items-center gap-2 rounded-lg bg-secondary text-sm text-foreground hover:bg-accent transition-colors">
                    {avatarUrl ? (
                      <img
                        src={avatarUrl}
                        alt={displayName}
                        className="w-6 h-6 rounded-full object-cover bg-primary/10 shrink-0"
                      />
                    ) : (
                      <div className="w-6 h-6 rounded-full bg-primary/10 flex items-center justify-center text-primary text-[10px] font-bold shrink-0">
                        {(displayName?.[0] ?? "U").toUpperCase()}
                      </div>
                    )}
                    <span className="text-xs font-semibold max-w-[100px] truncate">
                      {displayName}
                    </span>
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="end" className="w-56">
                  <DropdownMenuLabel className="space-y-0.5">
                    <div className="text-sm font-semibold text-foreground truncate">
                      {displayName}
                    </div>
                    <div className="text-xs text-muted-foreground truncate">
                      {user.email}
                    </div>
                    <div className="text-xs text-muted-foreground capitalize">
                      {roleLabel}
                    </div>
                  </DropdownMenuLabel>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem asChild>
                    <Link to="/profile-settings" className="cursor-pointer">
                      Profile Settings
                    </Link>
                  </DropdownMenuItem>
                  <DropdownMenuItem asChild>
                    <Link to="/password-change" className="cursor-pointer">
                      Password Change
                    </Link>
                  </DropdownMenuItem>
                  {user.role === "admin" && (
                    <DropdownMenuItem asChild>
                      <Link to="/settings" className="cursor-pointer">
                        System Settings
                      </Link>
                    </DropdownMenuItem>
                  )}
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    className="cursor-pointer text-destructive focus:text-destructive"
                    onClick={() => void logout()}
                  >
                    <LogOut size={16} className="mr-2" />
                    Logout
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </div>
          )}

          {/* Mobile Hamburger Menu Toggle */}
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="md:hidden w-9 h-9 flex items-center justify-center rounded-lg text-muted-foreground hover:bg-secondary hover:text-foreground transition-colors"
            aria-label="Toggle menu"
          >
            {mobileMenuOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile Drawer Dropdown */}
      {mobileMenuOpen && (
        <div className="md:hidden border-t border-border/60 bg-background px-6 py-4 space-y-4 animate-in slide-in-from-top-4 duration-200">
          <div className="flex flex-col gap-3 font-medium text-muted-foreground text-sm">
            <button
              onClick={() => {
                setMobileMenuOpen(false);
                if (window.location.pathname !== "/") {
                  navigate("/");
                } else {
                  window.scrollTo({ top: 0, behavior: "smooth" });
                }
              }}
              className="text-left py-1.5 hover:text-foreground transition-colors"
            >
              Home
            </button>
            <button
              onClick={() => handleScrollTo("#courses")}
              className="text-left py-1.5 hover:text-foreground transition-colors"
            >
              Courses
            </button>
            <Link
              to="/mock-tests"
              onClick={() => setMobileMenuOpen(false)}
              className="text-left py-1.5 hover:text-foreground transition-colors"
            >
              Mock Tests
            </Link>

            <button
              onClick={() => handleScrollTo("#contact")}
              className="text-left py-1.5 hover:text-foreground transition-colors"
            >
              Contact
            </button>
          </div>

          <div className="border-t border-border/40 pt-4 flex flex-col gap-2">
            {!user ? (
              <>
                <Link to="/login" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="outline" className="w-full justify-center">
                    Login
                  </Button>
                </Link>
                <Link to="/register" onClick={() => setMobileMenuOpen(false)}>
                  <Button className="w-full justify-center">Register</Button>
                </Link>
              </>
            ) : (
              <>
                <div className="flex items-center gap-2 px-1 py-1.5 text-foreground mb-1">
                  <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center text-primary text-xs font-bold shrink-0">
                    {(displayName?.[0] ?? "U").toUpperCase()}
                  </div>
                  <div className="flex flex-col leading-none">
                    <span className="text-xs font-bold truncate max-w-[180px]">
                      {displayName}
                    </span>
                    <span className="text-[10px] text-muted-foreground capitalize mt-0.5">
                      {user.role}
                    </span>
                  </div>
                </div>

                <Link to={startHref} onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="secondary" className="w-full justify-center gap-1.5">
                    <LayoutDashboard size={14} />
                    Go to Dashboard
                  </Button>
                </Link>

                <Link to="/profile-settings" onClick={() => setMobileMenuOpen(false)}>
                  <Button variant="ghost" className="w-full justify-start text-xs font-normal">
                    Profile Settings
                  </Button>
                </Link>

                <Button
                  variant="ghost"
                  className="w-full justify-start text-xs font-normal text-destructive hover:bg-destructive/10"
                  onClick={() => {
                    setMobileMenuOpen(false);
                    void logout();
                  }}
                >
                  <LogOut size={14} className="mr-2" />
                  Logout
                </Button>
              </>
            )}
          </div>
        </div>
      )}
    </nav>
  );
}
